import type { Role } from '@/features/auth/api';
import { apiFetch } from '@/lib/api/http';

export type EventStatus = 'draft' | 'published' | 'closed' | 'archived';

export type ParticipationFormat = 'offline' | 'online' | 'both';

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: '下書き',
  published: '公開済み',
  closed: '終了',
  archived: 'アーカイブ',
};

export const PARTICIPATION_FORMAT_LABELS: Record<ParticipationFormat, string> = {
  offline: 'オフライン',
  online: 'オンライン',
  both: '両方',
};

export interface EventItem {
  id: string;
  type_id: string;
  type_code: string;
  type_name: string;
  has_social: boolean;
  /** 種別が1対1の面談なら相手のロール。null は通常イベント（D-123） */
  counterpart_role: Role | null;
  title: string;
  description: string | null;
  /** 日時未定の面談がありうる（D-173） */
  starts_at: string | null;
  ends_at: string | null;
  venue: string | null;
  meet_url: string | null;
  capacity: number | null;
  social_capacity: number | null;
  status: EventStatus;
  published_at: string | null;
  /** 出欠を取らないイベントがある（D-117） */
  attendance_required: boolean;
  /** 出欠回答期限（出欠を取る通常イベント / D-285） */
  attendance_deadline_at: string | null;
  /** Meet参加ログを取るか。出欠とは別（D-225） */
  meet_log_enabled: boolean;
  /** オフライン / オンライン / 両方。両方なら受講生が出欠で選べる（D-223） */
  participation_format: ParticipationFormat;
  /** 課題/アンケート4種。false=この回では使わない（D-222） */
  pre_survey_enabled: boolean;
  pre_assignment_enabled: boolean;
  post_survey_enabled: boolean;
  post_assignment_enabled: boolean;
  day_survey_enabled: boolean;
  /** 理事・講師向けアンケート（月例会 / D-349） */
  director_survey_enabled: boolean;
  notifications_suppressed: boolean;
  target_count: number | null;
  /** 一覧の運営列（事務局のみ値が入る / D-110 A-4） */
  answered_count: number | null;
  unanswered_count: number | null;
  waitlist_count: number | null;
  /** 定員設定時のみ。確定参加数（D-336） */
  attending_count: number | null;
  /** 定員設定時のみ。定員いっぱいか（D-336） */
  capacity_full: boolean | null;
  /**
   * 席次の登録状態。システムでは扱わないため常に null（D-284。互換のためキーは残す）
   */
  seating_ready: boolean | null;
  /** 面談（サクセス面談・1on1）の相手と対象受講生の氏名。事務局のみ値が入る（D-177） */
  counterpart_name: string | null;
  member_name: string | null;
  /** 面談の対象受講生ID。詳細のみ・事務局とその回の担当者に返る（D-182） */
  member_id: string | null;
  /** 面談の相手（担当者）のユーザーID。事務局の候補日代理登録に使う（D-183） */
  counterpart_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

export interface EventQuery {
  page?: number;
  per_page?: number;
  q?: string;
  status?: EventStatus | null;
  /** 種別ごとの一覧（サイドバーの種別サブメニュー / D-184） */
  type_id?: string | null;
  /** 担当者ごとの面談一覧（事務局 / D-301） */
  staff_user_id?: string | null;
  counterpart_role?: Role | null;
  /** past=終了／upcoming=これから（日時未定含む） */
  schedule?: 'past' | 'upcoming' | null;
}

export function fetchEvents(query: EventQuery = {}): Promise<Page<EventItem>> {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.per_page) params.set('per_page', String(query.per_page));
  if (query.q) params.set('q', query.q);
  if (query.status) params.set('status', query.status);
  if (query.type_id) params.set('type_id', query.type_id);
  if (query.staff_user_id) params.set('staff_user_id', query.staff_user_id);
  if (query.counterpart_role) params.set('counterpart_role', query.counterpart_role);
  if (query.schedule) params.set('schedule', query.schedule);
  const search = params.toString();
  return apiFetch<Page<EventItem>>(`/events${search ? `?${search}` : ''}`);
}

export function fetchEvent(id: string): Promise<EventItem> {
  return apiFetch<EventItem>(`/events/${id}`);
}

export interface EventCreateInput {
  type_id: string;
  title: string;
  description?: string | null;
  /** 面談は日時未定で作成できる（D-173） */
  starts_at?: string | null;
  ends_at?: string | null;
  venue?: string | null;
  meet_url?: string | null;
  capacity?: number | null;
  social_capacity?: number | null;
  attendance_required?: boolean;
  attendance_deadline_at?: string | null;
  has_social?: boolean;
  participation_format?: ParticipationFormat;
  meet_log_enabled?: boolean;
  pre_survey_enabled?: boolean;
  pre_assignment_enabled?: boolean;
  post_survey_enabled?: boolean;
  post_assignment_enabled?: boolean;
  day_survey_enabled?: boolean;
  director_survey_enabled?: boolean;
  /** 1対1の面談のときだけ（D-118） */
  member_id?: string;
  counterpart_user_id?: string;
}

export function createEvent(input: EventCreateInput): Promise<EventItem> {
  return apiFetch<EventItem>('/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** 既存イベントを下書きとして複製する（D-220） */
export function copyEvent(id: string): Promise<EventItem> {
  return apiFetch<EventItem>(`/events/${id}/copy`, { method: 'POST' });
}

export interface EventRecurringCreateInput {
  type_id: string;
  title: string;
  description?: string | null;
  first_starts_at: string;
  duration_minutes: number;
  venue?: string | null;
  meet_url?: string | null;
  capacity?: number | null;
  social_capacity?: number | null;
  participation_format?: ParticipationFormat;
  meet_log_enabled?: boolean | null;
  interval: 'weekly' | 'monthly';
  occurrences: number;
}

export function createRecurringEvents(input: EventRecurringCreateInput): Promise<EventItem[]> {
  return apiFetch<EventItem[]>('/events/recurring', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateEvent(id: string, input: Partial<EventCreateInput>): Promise<EventItem> {
  return apiFetch<EventItem>(`/events/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function changeEventStatus(
  id: string,
  status: EventStatus,
  reason: string,
): Promise<EventItem> {
  return apiFetch<EventItem>(`/events/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  });
}

export function publishEvent(id: string): Promise<EventItem> {
  return apiFetch<EventItem>(`/events/${id}/publish`, { method: 'POST' });
}

export interface EventBulkPublishItem {
  event_id: string;
  title: string;
  ok: boolean;
  error: string | null;
}

export interface EventBulkPublishResult {
  published_count: number;
  failed_count: number;
  results: EventBulkPublishItem[];
}

export function bulkPublishEvents(eventIds: string[]): Promise<EventBulkPublishResult> {
  return apiFetch<EventBulkPublishResult>('/events/bulk-publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_ids: eventIds }),
  });
}

// --- 対象者（指示書 §4） -----------------------------------------------------
export interface EventTargetCriteria {
  cohort_ids: string[];
  course_ids: string[];
  statuses: string[];
  tags: string[];
  include_member_ids: string[];
  exclude_member_ids: string[];
}

export const EMPTY_TARGET_CRITERIA: EventTargetCriteria = {
  cohort_ids: [],
  course_ids: [],
  statuses: [],
  tags: [],
  include_member_ids: [],
  exclude_member_ids: [],
};

/** 対象者ひとり（D-132） */
export interface EventTargetMember {
  id: string;
  member_id: string;
  name: string;
  cohort_name: string | null;
  course_name: string | null;
  /** 公開後にグループから外した受講生（D-362） */
  is_removed?: boolean;
}

export interface EventTarget {
  event_id: string;
  criteria: EventTargetCriteria;
  matched_count: number;
  removed_count?: number;
  is_published: boolean;
  snapshot_taken_at: string | null;
  /** 対象者の名簿。公開前は今の条件に当てはまる人、公開後は公開時に確定した人（D-132） */
  members: EventTargetMember[];
}

export function fetchEventTarget(eventId: string): Promise<EventTarget> {
  return apiFetch<EventTarget>(`/events/${eventId}/target`);
}

export function setEventTarget(
  eventId: string,
  criteria: EventTargetCriteria,
): Promise<EventTarget> {
  return apiFetch<EventTarget>(`/events/${eventId}/target`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ criteria }),
  });
}

export function previewEventTarget(
  eventId: string,
  criteria: EventTargetCriteria,
): Promise<{ matched_count: number; members: EventTargetMember[] }> {
  return apiFetch(`/events/${eventId}/target/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(criteria),
  });
}

export function removeEventTargetMembers(
  eventId: string,
  memberIds: string[],
): Promise<EventTarget> {
  return apiFetch<EventTarget>(`/events/${eventId}/target/remove-members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

/** イベントの担当者（理事・講師／サクセス担当 / D-115） */
export interface EventStaffMember {
  user_id: string;
  display_name: string | null;
  email: string;
  role_label: string;
}

export interface EventStaffResponse {
  event_id: string;
  staff: EventStaffMember[];
}

export function fetchEventStaff(eventId: string): Promise<EventStaffResponse> {
  return apiFetch<EventStaffResponse>(`/events/${eventId}/staff`);
}

export function setEventStaff(eventId: string, userIds: string[]): Promise<EventStaffResponse> {
  return apiFetch<EventStaffResponse>(`/events/${eventId}/staff`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_ids: userIds }),
  });
}

/** イベントの当日写真（D-186）. */
export interface EventPhoto {
  id: string;
  url: string;
  original_name: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export function fetchEventPhotos(eventId: string): Promise<EventPhoto[]> {
  return apiFetch<EventPhoto[]>(`/events/${eventId}/photos`);
}

export function uploadEventPhoto(eventId: string, file: File): Promise<EventPhoto> {
  const body = new FormData();
  body.append('file', file);
  return apiFetch<EventPhoto>(`/events/${eventId}/photos`, { method: 'POST', body });
}

export function deleteEventPhoto(eventId: string, photoId: string): Promise<void> {
  return apiFetch<void>(`/events/${eventId}/photos/${photoId}`, { method: 'DELETE' });
}

export interface MeetLinkGuidePreview {
  subject: string;
  body: string;
  recipient_count: number;
  recipient_names: string[];
}

export interface MeetLinkGuideSendResult {
  sent: boolean;
  recipient_count: number;
  note: string | null;
}

export function fetchMeetLinkGuidePreview(eventId: string): Promise<MeetLinkGuidePreview> {
  return apiFetch<MeetLinkGuidePreview>(`/events/${eventId}/meet-link-guide/preview`);
}

export function sendMeetLinkGuide(eventId: string): Promise<MeetLinkGuideSendResult> {
  return apiFetch<MeetLinkGuideSendResult>(`/events/${eventId}/meet-link-guide/send`, {
    method: 'POST',
  });
}
